import {
	HalfFloatType,
	LinearSRGBColorSpace,
	NearestFilter,
	NoBlending,
	NoColorSpace,
	NodeMaterial,
	NodeUpdateType,
	QuadMesh,
	RenderTarget,
	RendererUtils,
	TempNode,
	Vector2
} from 'three/webgpu';
import {
	Fn,
	float,
	floor,
	fract,
	outputStruct,
	passTexture,
	property,
	textureBicubicLevel,
	uv,
	vec2,
	vec4
} from 'three/tsl';

/**
 * Purely spatial cloud upscaler. The low-resolution premultiplied cloud color
 * is reconstructed with Three.js' smooth four-fetch cubic B-spline filter.
 * Cloud feature depths stay nearest-sampled so empty sky depths never bleed
 * into occupied cloud pixels; opacity comes from the smooth color result.
 */
export class CloudSpatialUpscaleNode extends TempNode {

	constructor( colorNode, dataNode ) {

		super( 'vec4' );

		this.isCloudSpatialUpscaleNode = true;
		this.updateBeforeType = NodeUpdateType.FRAME;
		this.colorNode = colorNode;
		this.dataNode = dataNode;
		this._target = new RenderTarget( 1, 1, { depthBuffer: false, type: HalfFloatType, count: 2 } );
		this._target.textures[ 0 ].name = 'CyborgClouds spatially upscaled color';
		this._target.textures[ 1 ].name = 'CyborgClouds spatially upscaled data';
		this._target.textures[ 0 ].colorSpace = LinearSRGBColorSpace;
		this._target.textures[ 1 ].colorSpace = NoColorSpace;
		this._target.textures[ 1 ].minFilter = NearestFilter;
		this._target.textures[ 1 ].magFilter = NearestFilter;
		this._material = new NodeMaterial();
		this._material.name = 'CyborgClouds smooth spatial upscale';
		this._material.toneMapped = false;
		this._material.transparent = true;
		this._material.blending = NoBlending;
		this._quad = new QuadMesh();
		this._textureNode = passTexture( this, this._target.textures[ 0 ] );
		this._dataTextureNode = passTexture( this, this._target.textures[ 1 ] );
		this._rendererState = undefined;

	}

	getTextureNode() {

		return this._textureNode;

	}

	getDataTextureNode() {

		return this._dataTextureNode;

	}

	setSize( width, height ) {

		if ( this._target.width === width && this._target.height === height ) return;
		this._target.setSize( width, height );

	}

	updateBefore( frame ) {

		const { renderer } = frame;
		const size = renderer.getDrawingBufferSize( new Vector2() );
		this.setSize( size.width, size.height );

		this._rendererState = RendererUtils.resetRendererState( renderer, this._rendererState ?? undefined );
		renderer.setRenderTarget( this._target );
		this._quad.material = this._material;
		this._quad.name = 'CyborgClouds smooth spatial upscale';
		this._quad.render( renderer );
		RendererUtils.restoreRendererState( renderer, this._rendererState );

	}

	setup() {

		const colorOutput = property( 'vec4', 'cyborgSpatialColor' );
		const dataOutput = property( 'vec4', 'cyborgSpatialData' );
		const outputNode = outputStruct( colorOutput, dataOutput );
		const upscale = Fn( () => {

			const outputUV = uv();
			const smoothColor = textureBicubicLevel( this.colorNode.sample( outputUV ), float( 0 ) ).toVar();
			const smoothData = textureBicubicLevel( this.dataNode.sample( outputUV ), float( 0 ) );
			// The cloud color attachment is premultiplied over a render target and
			// its stored alpha is not the authoritative optical coverage. Coverage
			// lives in cloudData.b, so reconstruct that channel independently.
			const smoothAlpha = smoothData.b.clamp().toVar();

			// Interpolating raw cloud distance would pull the camera-far sentinel
			// into edge pixels. Instead, bilinearly reconstruct opacity-weighted
			// front/median distance from the four neighboring low-resolution cells.
			// This makes the later depth visibility continuous without allowing
			// empty sky pixels to cut square holes through the smooth cloud alpha.
			const sourceSize = vec2( this.dataNode.size() );
			const sourcePosition = outputUV.mul( sourceSize ).sub( 0.5 );
			const sourceBase = floor( sourcePosition );
			const sourceFraction = fract( sourcePosition );
			const inverseSourceSize = vec2( 1 ).div( sourceSize );
			const sampleData = ( x, y ) => this.dataNode.sample(
				sourceBase.add( vec2( x, y ) ).add( 0.5 ).mul( inverseSourceSize )
			);
			const data00 = sampleData( 0, 0 );
			const data10 = sampleData( 1, 0 );
			const data01 = sampleData( 0, 1 );
			const data11 = sampleData( 1, 1 );
			const weight00 = sourceFraction.x.oneMinus().mul( sourceFraction.y.oneMinus() );
			const weight10 = sourceFraction.x.mul( sourceFraction.y.oneMinus() );
			const weight01 = sourceFraction.x.oneMinus().mul( sourceFraction.y );
			const weight11 = sourceFraction.x.mul( sourceFraction.y );
			const opacityWeight00 = weight00.mul( data00.b );
			const opacityWeight10 = weight10.mul( data10.b );
			const opacityWeight01 = weight01.mul( data01.b );
			const opacityWeight11 = weight11.mul( data11.b );
			const depthWeight = opacityWeight00.add( opacityWeight10 ).add( opacityWeight01 ).add( opacityWeight11 );
			const fallbackData = this.dataNode.sample( outputUV );
			const frontDistance = depthWeight.greaterThan( 0.00001 ).select(
				data00.r.mul( opacityWeight00 )
					.add( data10.r.mul( opacityWeight10 ) )
					.add( data01.r.mul( opacityWeight01 ) )
					.add( data11.r.mul( opacityWeight11 ) )
					.div( depthWeight ),
				fallbackData.r
			);
			const medianDistance = depthWeight.greaterThan( 0.00001 ).select(
				data00.g.mul( opacityWeight00 )
					.add( data10.g.mul( opacityWeight10 ) )
					.add( data01.g.mul( opacityWeight01 ) )
					.add( data11.g.mul( opacityWeight11 ) )
					.div( depthWeight ),
				fallbackData.g
			);

			colorOutput.assign( vec4( smoothColor.rgb, smoothAlpha ) );
			dataOutput.assign( vec4(
				frontDistance,
				medianDistance,
				smoothAlpha,
				smoothAlpha.greaterThan( 0.00001 ).select( 1, 0 )
			) );
			return vec4( 0 );

		} );

		this._material.colorNode = upscale();
		this._material.outputNode = outputNode;

		return this._textureNode;

	}

	dispose() {

		this._target.dispose();
		this._material.dispose();

	}

}
